import {useState, useEffect, useRef, Dispatch, SetStateAction} from 'react'
import {v, debug, useForm, useTranslation, RIF, RequestResult, isConflictResult} from '../../lib'
import {selectTheme, createDispatchActions, selectProfile, selectIdentity, SecurityChangeKind} from '../../store'
import {
  CONFIRM_ITS_YOU_EMPTY_VALUE,
  ConfirmItsYouFieldErrors,
  ConfirmItsYouValue,
  readConfirmItsYouOutcome,
} from '../molecules/confirm_its_you_fields'
import {Button, Legend, Input, LabelBox, ConfirmItsYouFields, ProfileSettingsProcessString} from '..'

const schema = v.object({
  email: v.string().required(),
})

export interface NewEmailInputPagePropsT {
  verifyCode: string
  setProfileSettingsProcess: Dispatch<SetStateAction<ProfileSettingsProcessString>>
  setIncorrectCode: Dispatch<SetStateAction<boolean>>
}

export const NewEmailInputPage = (props: NewEmailInputPagePropsT) => {
  const {color, pad} = selectTheme()
  const {t} = useTranslation()

  const {verifyCode, setProfileSettingsProcess, setIncorrectCode} = props

  const profile = selectProfile()
  const identity = selectIdentity()
  const {doEMAIL_UPDATE_SUBMIT, doSESSION_LAST_SECURITY_CHANGE_SET} = createDispatchActions()
  const [requestResult, setRequestResult] = useState<RequestResult | null>(null)

  const [hasEmailRequiredError, setEmailRequiredError] = useState(false)
  const [hasEmailValidError, setEmailValidError] = useState(false)
  const [hasEmailConflictError, setEmailConflictError] = useState(false)
  const [hasSameEmailError, setSameEmailError] = useState(false)

  const mfaEnabled = identity?.mfaEnabled ?? false
  const [confirmValue, setConfirmValue] = useState<ConfirmItsYouValue>(CONFIRM_ITS_YOU_EMPTY_VALUE)
  const [fieldErrors, setFieldErrors] = useState<ConfirmItsYouFieldErrors>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [isSubmitting, setSubmitting] = useState(false)

  // `handleSubmit` validates asynchronously, so a second click can reach `onSubmit` before the
  // re-render that disables Done. The ref is what actually stops the second request; the disabled
  // state is what the user sees. Both matter: `verificationCode` is a one-time code, and sending
  // the same security change twice is two chances for the backend to end the other sessions.
  const submittingRef = useRef(false)
  // Whether the save is marked for the later sign-out message depends on the proof that was SENT.
  // The fields stay editable while the request is out, so the live `confirmValue` at reply time is
  // the wrong thing to ask. Only the verdict is kept, never the proof itself: the password and the
  // code stay in the molecule and nowhere else.
  const sentProofWasCompleteRef = useRef(false)

  const {register, handleSubmit, watch} = useForm({schema})

  const clearErrors = () => {
    setEmailRequiredError(false)
    setEmailValidError(false)
    setEmailConflictError(false)
    setSameEmailError(false)
    setFieldErrors({})
    setFormError(null)
  }

  const onSubmit = (data: any) => {
    if (submittingRef.current) return
    // Never `debug(data)` wholesale from here on: the proof must stay out of console output, and
    // the proof is not part of `data` -- it lives in `confirmValue` and goes straight to the saga.
    debug('newEmailInput.onSubmit', {email: data?.email})
    clearErrors()
    if (data?.email === profile?.email) {
      // nothing went out, so nothing is in flight and Done stays live
      setSameEmailError(true)
    } else {
      submittingRef.current = true
      setSubmitting(true)
      sentProofWasCompleteRef.current = confirmValue.proofIsComplete
      doEMAIL_UPDATE_SUBMIT({
        setRequestResult,
        payload: {
          // NOTE: this is the emailed one-time code, not an MFA code, and it goes out as the
          // string this component holds. Never convert it to a number on the way: these codes are
          // ten digits from `randomInt(10)`, so about one in ten starts with a zero that
          // `Number()` would eat, and the backend compares them as strings.
          verificationCode: verifyCode,
          email: data?.email,
          ...confirmValue.proof,
        },
      })
    }
  }

  const onError = (error: any) => {
    setEmailRequiredError(error?.email?.type == 'string.empty' ? true : false)
    setEmailValidError(error?.email?.type == 'string.email' ? true : false)
  }

  const backToVerifyPage = () => {
    setIncorrectCode(true)
    setProfileSettingsProcess('verify_code')
  }

  /**
   * The shared rules first -- the incomplete-proof mark and the refused proof read the same way in
   * both hosts of the confirm prompt -- then this host's own failure map, by status. The saga builds its REQUEST_ERROR
   * payload from an allowlist: a request that reached the server carries `statusCode` and the
   * server's `payload` and no `error` key at all, while a payload the saga's own schema rejected
   * carries `error` and no status. So branch on the status, and never reach through `error` -- it
   * is absent on every failure that is not a local schema rejection.
   */
  useEffect(() => {
    if (!requestResult) return
    submittingRef.current = false
    setSubmitting(false)

    const outcome = readConfirmItsYouOutcome(requestResult, {
      sentProofWasComplete: sentProofWasCompleteRef.current,
      t,
    })

    if (outcome.kind === 'saved') {
      // The user is never signed out from here. The proof in `confirmValue` dies with
      // this page, which unmounts as soon as the process moves on.
      if (outcome.recordIncompleteProof) {
        doSESSION_LAST_SECURITY_CHANGE_SET({kind: SecurityChangeKind.Email, at: Date.now()})
      }
      return setProfileSettingsProcess('email_changed')
    }
    // the proof was refused. Stay on the page with everything the user typed still here --
    // an expired authenticator code must not cost them the address and the password as well.
    if (outcome.kind === 'proof_refused') {
      setFieldErrors(outcome.fieldErrors)
      return setFormError(outcome.formError)
    }

    const result = requestResult.result as {statusCode?: number | null; payload?: unknown; error?: {isJoi?: boolean}}

    // The saga's schema turned the payload down before any request went out.
    if (result?.error?.isJoi) return applyLocalValidationError(result.error as {message?: string})

    const statusCode = result?.statusCode ?? null
    // Not a bare 410: the backend is renumbering conflict to 409 and answers with an envelope
    // whose `errorType` is CONFLICT, so `isConflictResult` keys on the name and keeps both status
    // numbers as a fallback (z91464gzar). A conflict this does not recognise falls through to the
    // outage line and the user never learns the address is taken.
    if (isConflictResult(requestResult.result)) return setEmailConflictError(true)
    // The emailed one-time code is the thing that expired, so this is the one worth restarting.
    if (statusCode === 401) return backToVerifyPage()
    // Everything left -- a 5xx, and the `null` status of a request that never got an answer --
    // is an outage, in the same words as the ordinary-503 notice. It is not a wrong verification
    // code, so it must not
    // throw the user back to the start of the flow.
    return setFormError(t('connection.unavailable_message'))
  }, [requestResult])

  /**
   * Joi's message is rebuilt by `toProofSafeSchemaError` from the constraint text and never
   * echoes a submitted value, but it is still developer prose, so it is matched rather than
   * shown. Anything it does not recognise gets the neutral retry line.
   */
  const applyLocalValidationError = (error: {message?: string}) => {
    const message = error?.message ?? ''
    if (/must be a valid email/i.test(message)) return setEmailValidError(true)
    if (/"email".*not allowed to be empty/i.test(message)) return setEmailRequiredError(true)
    return setFormError(t('security_change.error_message'))
  }

  const emailTyped = watch('email')?.length > 0
  const submitBlocked = !confirmValue.canSubmit || isSubmitting

  return (
    <div
      data-testid='new_email_input_page'
      css={{
        width: 'fit-content',
        margin: '20vh auto',
        padding: '50px',
        position: 'relative',
      }}>
      {/* The whole flow is one form, so one submit carries the code, the address and the
          proof. `autoComplete='off'` keeps a password manager from offering to save the new
          address as a username; the proof fields set their own autocomplete and override it. */}
      <form onSubmit={handleSubmit(onSubmit, onError)} autoComplete='off' css={{width: '480px'}}>
        <div>
          <Legend>Enter Your New Email</Legend>
          <div css={{marginTop: '20px'}}>
            <LabelBox
              {...{
                label: 'New Email',
                warningMsg: hasEmailRequiredError
                  ? 'Email Required'
                  : hasEmailValidError
                    ? 'Must be a valid email'
                    : hasEmailConflictError
                      ? 'An account with this email already exists'
                      : hasSameEmailError
                        ? "You're already using this email. Please choose another."
                        : '',
              }}>
              <Input
                data-testid='new_email_input'
                {...register('email')}
                css={{marginBottom: pad.large}}
                type='email'
                autoComplete='off'
                placeholder='name@email.com'
              />
            </LabelBox>
          </div>
        </div>

        {/* The confirm prompt's second host. The proof is what keeps the other sessions alive, so a
            blanket "you may be signed out everywhere" warning would be untrue here; what stays true
            for an MFA identity is the narrower SMS/email warning inside the prompt. */}
        <div css={{color: color.grey_400, marginBottom: pad.medium}}>{t('security_change.confirm_intro')}</div>
        <ConfirmItsYouFields
          {...{
            mfaEnabled,
            fieldErrors,
            onChange: setConfirmValue,
          }}
        />
        {RIF(
          formError,
          <div data-testid='new_email_form_error' css={{color: color.warning, marginBottom: pad.medium}}>
            {formError}
          </div>,
        )}

        <div css={{display: 'flex', alignItems: 'center'}}>
          <Button
            type='submit'
            disabled={submitBlocked}
            css={{
              marginRight: pad.medium,
              background: emailTyped && !submitBlocked ? color.primary : color.grey_160,
            }}>
            Done
          </Button>
        </div>
      </form>
    </div>
  )
}
